import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  getCognitoUserData,
  loginUser,
  logoutUser,
} from "../services/authService";

interface AuthContextType {
  loggedIn: boolean;
  role: "Admin" | "User" | null;
  user: any | null;
  initialized: boolean;
  login: (
    email: string,
    password: string,
    rememberMe: boolean
  ) => Promise<"Admin" | "User">;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [role, setRole] = useState<"Admin" | "User" | null>(null);
  const [user, setUser] = useState<any>(null);
  const [initialized, setInitialized] = useState(false);

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean
  ): Promise<"Admin" | "User"> => {
    await loginUser(email, password);
    localStorage.setItem("email", email);
    localStorage.setItem("rememberMe", rememberMe.toString());

    const userData = await getCognitoUserData();
    const group = userData["cognito:groups"]?.[0];
    const resolvedRole = group === "Admin" ? "Admin" : "User";

    setUser(userData);
    setRole(resolvedRole);
    setLoggedIn(true);

    return resolvedRole;
  };

  const logout = async () => {
    try {
      const email = localStorage.getItem("email") || "";
      await logoutUser(email);
    } catch (error) {
      console.warn("Logout failed or already invalidated");
    } finally {
      localStorage.removeItem("email");
      localStorage.removeItem("rememberMe");

      setLoggedIn(false);
      setRole(null);
      setUser(null);
    }
  };

  // Auto-login if rememberMe
  useEffect(() => {
    const email = localStorage.getItem("email");
    const remember = localStorage.getItem("rememberMe") === "true";

    if (email && remember) {
      getCognitoUserData()
        .then((userData) => {
          const group = userData["cognito:groups"]?.[0];
          const roleValue = group === "Admin" ? "Admin" : "User";

          setUser(userData);
          setRole(roleValue);
          setLoggedIn(true);
        })
        .catch(() => logout())
        .finally(() => setInitialized(true));
    } else {
      setInitialized(true); // no auto-login, but we're ready
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ loggedIn, role, user, initialized, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
